/**
 * CZNA-1596 (UC PC-08) — react-query hooks over public error reports.
 *
 * <p>Every key starts with `error-reports`, so the one invalidation after a status change
 * (react-query matches by prefix) refreshes the list, the detail, the rail/tile count and the
 * record-detail banner together — closing the last open report makes the banner go away.
 *
 * <p>Whether the user handles reports at all is the backend's answer (`summary.canHandle`), not
 * something rebuilt here from role rows (CZNA-598).
 */
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { changeErrorReportStatus, fetchErrorReport, fetchErrorReports, fetchErrorReportSummary, fetchRecordOpenErrorReports, } from '../api/errorReportService';
export var ERROR_REPORTS_KEY = 'error-reports';
/** A 403/404 is a definitive answer; asking again only delays the message (CZNA-696). */
var retryUnlessDefinitive = function (failureCount, err) {
    var _a;
    var status = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status;
    if (status === 403 || status === 404)
        return false;
    return failureCount < 3;
};
export var useErrorReportSummary = function () {
    var _a, _b;
    var ky = useOkapiKy();
    var _c = useQuery([ERROR_REPORTS_KEY, 'summary'], function () { return fetchErrorReportSummary(ky); }, { retry: retryUnlessDefinitive }), data = _c.data, isLoading = _c.isLoading;
    return {
        canHandle: (_a = data === null || data === void 0 ? void 0 : data.canHandle) !== null && _a !== void 0 ? _a : false,
        newCount: (_b = data === null || data === void 0 ? void 0 : data.newCount) !== null && _b !== void 0 ? _b : 0,
        isLoading: isLoading,
    };
};
export var useErrorReportList = function (params) {
    var _a, _b;
    var ky = useOkapiKy();
    var _c = useQuery([ERROR_REPORTS_KEY, 'list', params], function () { return fetchErrorReports(ky, params); }, { keepPreviousData: true, retry: retryUnlessDefinitive }), data = _c.data, isLoading = _c.isLoading, isFetching = _c.isFetching, isError = _c.isError, error = _c.error;
    return {
        items: (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : [],
        totalRecords: (_b = data === null || data === void 0 ? void 0 : data.totalRecords) !== null && _b !== void 0 ? _b : 0,
        isLoading: isLoading,
        isFetching: isFetching,
        isError: isError,
        error: error,
    };
};
export var useErrorReportDetail = function (id) {
    var ky = useOkapiKy();
    var _a = useQuery([ERROR_REPORTS_KEY, 'detail', id], function () { return fetchErrorReport(ky, id); }, { enabled: !!id, retry: retryUnlessDefinitive }), data = _a.data, isLoading = _a.isLoading, isError = _a.isError, error = _a.error;
    return { report: data, isLoading: isLoading, isError: isError, error: error };
};
export var useChangeErrorReportStatus = function (id) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (body) { return changeErrorReportStatus(ky, id, body); }, {
        onSuccess: function () {
            queryClient.invalidateQueries([ERROR_REPORTS_KEY]);
        },
    });
};
export var useRecordOpenErrorReports = function (recordId) {
    var _a, _b;
    var ky = useOkapiKy();
    var data = useQuery([ERROR_REPORTS_KEY, 'record', recordId], function () { return fetchRecordOpenErrorReports(ky, recordId); }, { enabled: !!recordId, retry: retryUnlessDefinitive }).data;
    return {
        openCount: (_a = data === null || data === void 0 ? void 0 : data.openCount) !== null && _a !== void 0 ? _a : 0,
        canOpen: (_b = data === null || data === void 0 ? void 0 : data.canOpen) !== null && _b !== void 0 ? _b : false,
    };
};
